// src/components/work_orders/WorkOrderFeedback.js
// Enhanced feedback component with advanced debugging and modern UI

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  VStack,
  HStack,
  Textarea,
  Button,
  Spinner,
  useToast,
  Divider,
  Tag,
  Flex,
  Icon,
  useColorModeValue,
  IconButton,
  Heading,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Tooltip,
  ScaleFade,
  Fade,
  Stack
} from '@chakra-ui/react';
import { StarIcon } from '@chakra-ui/icons';
import { MdPerson, MdAccessTime, MdRateReview, MdSend, MdRefresh } from 'react-icons/md';
import feedbackService from '../../services/feedbackService';
import { format } from 'date-fns';

// ============================================================================
// DEBUGGING CONFIGURATION
// ============================================================================

const DEBUG_CONFIG = {
  enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
  logLevel: 'info', // 'info', 'warn', 'error'
  logToConsole: true,
  logToUI: false, // Set to true to see debug info in UI
};

/**
 * Advanced debug logger with levels and context
 */
const debugLog = {
  info: (component, message, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    const timestamp = new Date().toISOString();
    console.log(
      `%c[${timestamp}] [${component}] ℹ️ ${message}`,
      'color: #3182CE; font-weight: bold;',
      data || ''
    );
  },
  warn: (component, message, data = null) => {
    if (!DEBUG_CONFIG.enabled) return;
    const timestamp = new Date().toISOString();
    console.warn(
      `%c[${timestamp}] [${component}] ⚠️ ${message}`,
      'color: #D69E2E; font-weight: bold;',
      data || ''
    );
  },
  error: (component, message, error = null) => {
    if (!DEBUG_CONFIG.enabled) return;
    const timestamp = new Date().toISOString();
    console.error(
      `%c[${timestamp}] [${component}] ❌ ${message}`,
      'color: #E53E3E; font-weight: bold;',
      error || ''
    );
    if (error?.stack) {
      console.error('Stack trace:', error.stack);
    }
  },
  network: (component, action, endpoint, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    const timestamp = new Date().toISOString();
    console.log(
      `%c[${timestamp}] [${component}] 🌐 ${action} ${endpoint}`,
      'color: #805AD5; font-weight: bold;',
      data || ''
    );
  },
  render: (component, phase, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    const timestamp = new Date().toISOString();
    console.log(
      `%c[${timestamp}] [${component}] 🎨 ${phase}`,
      'color: #38B2AC; font-weight: bold;',
      data || ''
    );
  }
};

// ============================================================================
// RATING COMPONENT
// ============================================================================

const Rating = ({ value, interactive = false, onChange, size = 'md' }) => {
  const iconSize = size === 'lg' ? 6 : size === 'md' ? 5 : 4;
  const activeColor = useColorModeValue('yellow.400', 'yellow.300');
  const inactiveColor = useColorModeValue('gray.300', 'gray.600');

  debugLog.render('Rating', 'Rendering', { value, interactive, size });

  return (
    <HStack spacing={1}>
      {Array.from({ length: 5 }, (_, i) => (
        interactive ? (
          <IconButton
            key={i}
            icon={<StarIcon />}
            onClick={() => {
              debugLog.info('Rating', `Star ${i + 1} clicked`);
              onChange?.(i + 1);
            }}
            color={i < value ? activeColor : inactiveColor}
            variant="ghost"
            size={size}
            _hover={{
              transform: 'scale(1.2)',
              color: activeColor
            }}
            transition="all 0.2s"
            aria-label={`Rate ${i + 1} star${i !== 0 ? 's' : ''}`}
          />
        ) : (
          <Icon
            key={i}
            as={StarIcon}
            color={i < value ? activeColor : inactiveColor}
            boxSize={iconSize}
          />
        )
      ))}
    </HStack>
  );
};

Rating.propTypes = {
  value: PropTypes.number.isRequired,
  interactive: PropTypes.bool,
  onChange: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md', 'lg'])
};

// ============================================================================
// FEEDBACK CARD COMPONENT
// ============================================================================

const FeedbackCard = ({ feedback }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const commentBg = useColorModeValue('gray.50', 'gray.600');
  const sourceColor = feedback.source === 'technician' ? 'blue' : 'purple';
  // --- UPDATE: null-safe source / date (an empty source or bad timestamp crashed the whole tab)
  const sourceLabel = feedback.source ? feedback.source.charAt(0).toUpperCase() + feedback.source.slice(1) : 'Feedback';
  const created = feedback.created_at ? new Date(feedback.created_at) : null;
  const createdLabel = created && !Number.isNaN(created.getTime()) ? format(created, 'MMM d, yyyy h:mm a') : '—';
  // --- END UPDATE

  debugLog.render('FeedbackCard', 'Rendering feedback', {
    id: feedback.id,
    source: feedback.source,
    rating: feedback.rating
  });

  return (
    <ScaleFade in={true} initialScale={0.95}>
      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={5}
        bg={cardBg}
        w="100%"
        transition="all 0.2s"
        _hover={{
          shadow: 'md',
          transform: 'translateY(-2px)'
        }}
      >
        <VStack align="stretch" spacing={3}>
          {/* Header */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <HStack spacing={3}>
              <Tag
                size="md"
                colorScheme={sourceColor}
                variant="solid"
                borderRadius="full"
              >
                {sourceLabel}{/* --- UPDATE */}
              </Tag>
              <HStack spacing={2}>
                <Icon as={MdPerson} color={mutedTextColor} boxSize={4} />
                <Text fontWeight="semibold" color={textColor}>
                  {feedback.username || 'System User'}
                </Text>
              </HStack>
            </HStack>
            <HStack spacing={2} color={mutedTextColor}>
              <Icon as={MdAccessTime} boxSize={4} />
              <Text fontSize="sm">
                {createdLabel}{/* --- UPDATE */}
              </Text>
            </HStack>
          </Flex>

          {/* Rating */}
          <Box>
            <Text fontSize="xs" color={mutedTextColor} mb={1}>Rating</Text>
            <Rating value={Number(feedback.rating) || 0} />{/* --- UPDATE */}
          </Box>

          {/* Comments */}
          {feedback.comments && (
            <>
              <Divider />
              <Box>
                <Text fontSize="xs" color={mutedTextColor} mb={2}>
                  <Icon as={MdRateReview} display="inline" mr={1} />
                  Comments
                </Text>
                <Text
                  fontSize="sm"
                  color={textColor}
                  fontStyle="italic"
                  lineHeight="1.6"
                  p={3}
                  bg={commentBg}
                  borderRadius="md"
                >
                  "{feedback.comments}"
                </Text>
              </Box>
            </>
          )}
        </VStack>
      </Box>
    </ScaleFade>
  );
};

FeedbackCard.propTypes = {
  feedback: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    source: PropTypes.string,          // --- UPDATE: optional
    username: PropTypes.string,
    created_at: PropTypes.string,
    rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    comments: PropTypes.string
  }).isRequired
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const WorkOrderFeedback = ({ workOrder }) => {
  // State
  const [feedbackList, setFeedbackList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const toast = useToast();

  // Theme colors
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const emptyStateColor = useColorModeValue('gray.400', 'gray.500');

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchFeedback = useCallback(async () => {
    if (!workOrder?.id) {
      debugLog.warn('WorkOrderFeedback', 'fetchFeedback called without work order ID');
      return;
    }

    const startTime = performance.now();
    debugLog.network('WorkOrderFeedback', 'GET', `/feedback/${workOrder.id}`);
    
    setIsLoading(true);
    setError(null);

    try {
      const data = await feedbackService.getFeedbackForWorkOrder(workOrder.id);
      
      const duration = performance.now() - startTime;
      debugLog.info(
        'WorkOrderFeedback',
        `Feedback loaded successfully in ${duration.toFixed(2)}ms`,
        { count: data?.length || 0 }
      );

      setFeedbackList(Array.isArray(data) ? data : []);

    } catch (error) {
      const duration = performance.now() - startTime;
      debugLog.error(
        'WorkOrderFeedback',
        `Failed to load feedback after ${duration.toFixed(2)}ms`,
        error
      );

      setError(error.message || 'Failed to load feedback');
      setFeedbackList([]);

      toast({
        title: 'Error Loading Feedback',
        description: error.message || 'Could not retrieve feedback for this work order.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

    } finally {
      setIsLoading(false);
    }
  }, [workOrder?.id, toast]);

  useEffect(() => {
    debugLog.info('WorkOrderFeedback', 'Component mounted/updated', {
      workOrderId: workOrder?.id,
      workOrderStatus: workOrder?.status
    });

    if (workOrder?.id) {
      fetchFeedback();
    }
  }, [workOrder?.id, fetchFeedback]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSubmit = async () => {
    debugLog.info('WorkOrderFeedback', 'Submit feedback initiated', {
      rating: newRating,
      hasComments: !!newComment
    });

    // Validation
    if (newRating === 0) {
      debugLog.warn('WorkOrderFeedback', 'Validation failed: No rating selected');
      toast({
        title: 'Rating Required',
        description: 'Please select a rating before submitting.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    const startTime = performance.now();
    setIsSubmitting(true);

    try {
      const payload = {
        work_order_id: workOrder.id,
        comments: newComment.trim() || undefined,
        rating: newRating,
      };

      debugLog.network('WorkOrderFeedback', 'POST', '/feedback', payload);

      await feedbackService.submitAdminFeedback(payload);

      const duration = performance.now() - startTime;
      debugLog.info(
        'WorkOrderFeedback',
        `Feedback submitted successfully in ${duration.toFixed(2)}ms`
      );

      toast({
        title: 'Feedback Submitted',
        description: 'Your feedback has been recorded successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setNewComment('');
      setNewRating(0);

      // Refresh feedback list
      fetchFeedback();

    } catch (error) {
      const duration = performance.now() - startTime;
      debugLog.error(
        'WorkOrderFeedback',
        `Feedback submission failed after ${duration.toFixed(2)}ms`,
        error
      );

      toast({
        title: 'Submission Failed',
        description: error.message || 'Could not save your feedback.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    debugLog.info('WorkOrderFeedback', 'Manual refresh triggered');
    fetchFeedback();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  debugLog.render('WorkOrderFeedback', 'Main render', {
    isLoading,
    feedbackCount: feedbackList.length,
    canSubmit: workOrder?.status === 'completed'
  });

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Icon as={MdRateReview} boxSize={5} color={headingColor} />
          <Heading size="sm" color={headingColor}>
            Feedback
          </Heading>
          {feedbackList.length > 0 && (
            <Badge colorScheme="blue" fontSize="xs" borderRadius="full">
              {feedbackList.length}
            </Badge>
          )}
        </HStack>
        <Tooltip label="Refresh feedback">
          <IconButton
            icon={<Icon as={MdRefresh} />}
            size="sm"
            variant="ghost"
            onClick={handleRefresh}
            isLoading={isLoading}
            aria-label="Refresh feedback"
          />
        </Tooltip>
      </Flex>

      {/* Feedback List */}
      <VStack spacing={4} align="stretch" mb={6}>
        {isLoading ? (
          <Flex justify="center" align="center" minH="150px">
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color={mutedTextColor} fontSize="sm">
                Loading feedback...
              </Text>
            </VStack>
          </Flex>
        ) : error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box flex={1}>
              <AlertDescription>{error}</AlertDescription>
            </Box>
            <Button size="sm" onClick={handleRefresh} ml={2}>
              Retry
            </Button>
          </Alert>
        ) : feedbackList.length > 0 ? (
          <Fade in={true}>
            <VStack spacing={3} align="stretch">
              {feedbackList.map((fb) => (
                <FeedbackCard key={fb.id} feedback={fb} />
              ))}
            </VStack>
          </Fade>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="150px"
            p={6}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            borderStyle="dashed"
          >
            <Icon as={MdRateReview} boxSize={12} color={emptyStateColor} mb={3} />
            <Text color={emptyStateColor} fontSize="sm" textAlign="center">
              No feedback has been submitted for this work order yet.
            </Text>
          </Flex>
        )}
      </VStack>

      {/* Add Feedback Form - Only for completed work orders */}
      {['completed', 'closed'].includes(workOrder?.status) && ( /* --- UPDATE: closed jobs can be rated too */
        <Fade in={true}>
          <Box
            mt={8}
            p={5}
            bg={cardBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="sm" color={headingColor} mb={4}>
              Add Your Feedback
            </Heading>

            <VStack spacing={4} align="stretch">
              {/* Rating Input */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  Your Rating *
                </Text>
                <Rating
                  value={newRating}
                  interactive
                  onChange={setNewRating}
                  size="lg"
                />
                {newRating > 0 && (
                  <Text fontSize="xs" color={mutedTextColor} mt={1}>
                    {newRating} star{newRating !== 1 ? 's' : ''} selected
                  </Text>
                )}
              </Box>

              {/* Comments Input */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  Comments (Optional)
                </Text>
                <Textarea
                  placeholder="Share your thoughts about the work order resolution..."
                  value={newComment}
                  onChange={(e) => {
                    setNewComment(e.target.value);
                    debugLog.info('WorkOrderFeedback', 'Comment updated', {
                      length: e.target.value.length
                    });
                  }}
                  rows={4}
                  resize="vertical"
                  maxLength={1000}
                />
                <Text fontSize="xs" color={mutedTextColor} mt={1} textAlign="right">
                  {newComment.length} / 1000 characters
                </Text>
              </Box>

              {/* Submit Button */}
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting..."
                isDisabled={newRating === 0}
                leftIcon={<Icon as={MdSend} />}
                alignSelf="flex-start"
              >
                Submit Feedback
              </Button>

              {newRating === 0 && (
                <Text fontSize="xs" color="orange.500">
                  * Please select a rating to submit feedback
                </Text>
              )}
            </VStack>
          </Box>
        </Fade>
      )}

      {/* Debug Info (only in development) */}
      {DEBUG_CONFIG.enabled && DEBUG_CONFIG.logToUI && (
        <Box
          mt={4}
          p={3}
          bg="gray.800"
          color="green.300"
          borderRadius="md"
          fontFamily="mono"
          fontSize="xs"
        >
          <Text fontWeight="bold" mb={2}>🐛 Debug Info</Text>
          <Stack spacing={1}>
            <Text>Work Order ID: {workOrder?.id || 'N/A'}</Text>
            <Text>Status: {workOrder?.status || 'N/A'}</Text>
            <Text>Feedback Count: {feedbackList.length}</Text>
            <Text>Loading: {isLoading ? 'Yes' : 'No'}</Text>
            <Text>Submitting: {isSubmitting ? 'Yes' : 'No'}</Text>
            <Text>Error: {error || 'None'}</Text>
            <Text>New Rating: {newRating}/5</Text>
            <Text>Comment Length: {newComment.length}</Text>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

WorkOrderFeedback.propTypes = {
  workOrder: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // --- UPDATE: numeric ids
    status: PropTypes.string,
  }).isRequired
};

export default WorkOrderFeedback;